// Primitivas de interfaz: tarjeta, KPI, pestañas, diálogo, campo, avatar, botón de icono, popover y menú.
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Icono, type NombreIcono } from './Iconos';
import { iniciales } from '../lib/formato';

export { Icono } from './Iconos';
export type { NombreIcono } from './Iconos';

export type TonoBaldosa = 'naranja' | 'morado' | 'verde' | 'azul' | 'rojo' | 'gris' | 'cian';

export function Baldosa({ icono, tono = 'azul', chica }: { icono: NombreIcono; tono?: TonoBaldosa; chica?: boolean }) {
  return (
    <span className={`baldosa baldosa--${tono} ${chica ? 'baldosa--chica' : ''}`} aria-hidden="true">
      <Icono nombre={icono} tam={16} />
    </span>
  );
}

export function Tarjeta({
  titulo,
  subtitulo,
  acciones,
  children,
  className = '',
  relleno = true,
  icono,
  tono,
}: {
  titulo?: ReactNode;
  subtitulo?: ReactNode;
  acciones?: ReactNode;
  children: ReactNode;
  className?: string;
  relleno?: boolean;
  icono?: NombreIcono;
  tono?: TonoBaldosa;
}) {
  return (
    <section className={`tarjeta ${relleno ? '' : 'tarjeta--sin-relleno'} ${className}`}>
      {(titulo || acciones) && (
        <header className="tarjeta__cabecera">
          <div className={icono ? 'tarjeta__titulo-fila' : undefined}>
            {icono && <Baldosa icono={icono} tono={tono} />}
            <div>
              {titulo && <h2 className="tarjeta__titulo">{titulo}</h2>}
              {subtitulo && <p className="tarjeta__subtitulo">{subtitulo}</p>}
            </div>
          </div>
          {acciones && <div className="tarjeta__acciones">{acciones}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Kpi({
  etiqueta,
  valor,
  detalle,
  tono,
}: {
  etiqueta: string;
  valor: ReactNode;
  detalle?: ReactNode;
  tono?: 'alerta' | 'peligro' | 'exito';
}) {
  return (
    <div className={`kpi ${tono ? `kpi--${tono}` : ''}`}>
      <span className="kpi__etiqueta">{etiqueta}</span>
      <span className="kpi__valor">{valor}</span>
      {detalle && <span className="kpi__detalle">{detalle}</span>}
    </div>
  );
}

export interface Pestana {
  clave: string;
  titulo: string;
  oculta?: boolean;
  contador?: number;
  icono?: NombreIcono;
  /** Texto para anchos reducidos (la vista del caso en panel). */
  tituloCorto?: string;
}

/** Pestañas con navegación por flechas (patrón WAI-ARIA tabs). */
export function Pestanas({
  pestanas,
  activa,
  alCambiar,
  etiqueta = 'Secciones',
  compactas,
}: {
  pestanas: Pestana[];
  activa: string;
  alCambiar: (clave: string) => void;
  etiqueta?: string;
  compactas?: boolean;
}) {
  const visibles = pestanas.filter((p) => !p.oculta);
  const ref = useRef<HTMLDivElement>(null);
  const alTecla = (e: ReactKeyboardEvent) => {
    const i = visibles.findIndex((p) => p.clave === activa);
    let n = -1;
    if (e.key === 'ArrowRight') n = (i + 1) % visibles.length;
    if (e.key === 'ArrowLeft') n = (i - 1 + visibles.length) % visibles.length;
    if (e.key === 'Home') n = 0;
    if (e.key === 'End') n = visibles.length - 1;
    if (n >= 0) {
      e.preventDefault();
      alCambiar(visibles[n].clave);
      requestAnimationFrame(() => ref.current?.querySelectorAll<HTMLButtonElement>('[role=tab]')[n]?.focus());
    }
  };
  return (
    <div className={`pestanas ${compactas ? 'pestanas--compactas' : ''}`} role="tablist" aria-label={etiqueta} ref={ref} onKeyDown={alTecla}>
      {visibles.map((p) => (
        <button
          key={p.clave}
          role="tab"
          type="button"
          aria-selected={p.clave === activa}
          tabIndex={p.clave === activa ? 0 : -1}
          className={`pestanas__item ${p.clave === activa ? 'pestanas__item--activa' : ''}`}
          onClick={() => alCambiar(p.clave)}
        >
          {p.icono && <Icono nombre={p.icono} />}
          {p.tituloCorto ? (
            <>
              <span className="pestanas__largo">{p.titulo}</span>
              <span className="pestanas__corto" aria-hidden="true">
                {p.tituloCorto}
              </span>
            </>
          ) : (
            p.titulo
          )}
          {p.contador !== undefined && p.contador > 0 && <span className="pestanas__contador">{p.contador}</span>}
        </button>
      ))}
    </div>
  );
}

export function Dialogo({
  abierto,
  titulo,
  alCerrar,
  children,
  pie,
  ancho = 560,
  className = '',
}: {
  abierto: boolean;
  titulo: ReactNode;
  alCerrar: () => void;
  children: ReactNode;
  pie?: ReactNode;
  ancho?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (abierto && !d.open) d.showModal();
    if (!abierto && d.open) d.close();
  }, [abierto]);
  return (
    <dialog
      ref={ref}
      className={`dialogo ${className}`}
      aria-labelledby={id}
      style={{ maxWidth: ancho }}
      onCancel={(e) => {
        e.preventDefault();
        alCerrar();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') e.stopPropagation();
      }}
    >
      {abierto && (
        <>
          <header className="dialogo__cabecera">
            <h2 id={id}>{titulo}</h2>
            <BotonIcono icono="cerrar" etiqueta="Cerrar" onClick={alCerrar} />
          </header>
          <div className="dialogo__cuerpo">{children}</div>
          {pie && <footer className="dialogo__pie">{pie}</footer>}
        </>
      )}
    </dialog>
  );
}

export function Campo({
  etiqueta,
  ayuda,
  error,
  requerido,
  children,
  ancho,
}: {
  etiqueta: string;
  ayuda?: ReactNode;
  error?: string | null;
  requerido?: boolean;
  children: (id: string) => ReactNode;
  ancho?: 'completo';
}) {
  const id = useId();
  return (
    <div className={`campo ${error ? 'campo--error' : ''} ${ancho === 'completo' ? 'campo--completo' : ''}`}>
      <label htmlFor={id} className="campo__etiqueta">
        {etiqueta}
        {requerido && (
          <span className="campo__req" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {children(id)}
      {error ? (
        <span className="campo__error" role="alert">
          {error}
        </span>
      ) : ayuda ? (
        <span className="campo__ayuda">{ayuda}</span>
      ) : null}
    </div>
  );
}

export function DatoDefinicion({ termino, children }: { termino: string; children: ReactNode }) {
  return (
    <div className="dato">
      <dt>{termino}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// ------------------------------------------------------------------ avatar
const PALETA_AVATAR = ['#3949AB', '#00796B', '#6A1B9A', '#AD1457', '#2E7D32', '#5D4037', '#0277BD', '#C62828', '#4E342E', '#283593'];

function colorDe(clave: string) {
  let h = 0;
  for (const ch of clave) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETA_AVATAR[h % PALETA_AVATAR.length];
}

export function Avatar({
  nombre,
  oid,
  tam = 24,
  titulo = true,
  className = '',
}: {
  nombre?: string | null;
  oid?: string | null;
  tam?: 16 | 20 | 24 | 28 | 32 | 40;
  titulo?: boolean;
  className?: string;
}) {
  if (!nombre && !oid) {
    return (
      <span className={`avatar avatar--vacio avatar--${tam} ${className}`} title={titulo ? 'Sin asignar' : undefined} role="img" aria-label="Sin asignar">
        <Icono nombre="usuario" tam={tam <= 20 ? 12 : 14} />
      </span>
    );
  }
  const n = nombre || oid || '';
  if (oid === 'sistema' || n === 'Sistema') {
    return (
      <span className={`avatar avatar--${tam} avatar--sistema ${className}`} title={titulo ? 'Sistema (agentes)' : undefined} role="img" aria-label="Sistema">
        <Icono nombre="rayo" tam={tam <= 20 ? 12 : 16} />
      </span>
    );
  }
  return (
    <span
      className={`avatar avatar--${tam} ${className}`}
      style={{ background: colorDe(oid || n) }}
      title={titulo ? n : undefined}
      role="img"
      aria-label={n}
    >
      {n === 'Sistema' ? 'S' : iniciales(n)}
    </span>
  );
}

export function GrupoAvatares({ personas, max = 4, tam = 24 }: { personas: { oid: string; nombre?: string | null }[]; max?: number; tam?: 24 | 28 | 32 }) {
  const visibles = personas.slice(0, max);
  const resto = personas.length - visibles.length;
  return (
    <span className="grupo-avatares">
      {visibles.map((p) => (
        <Avatar key={p.oid} oid={p.oid} nombre={p.nombre ?? p.oid} tam={tam} />
      ))}
      {resto > 0 && (
        <span className={`avatar avatar--${tam} avatar--resto`} title={`${resto} más`}>
          +{resto}
        </span>
      )}
    </span>
  );
}

// ------------------------------------------------------------------ botones
/** Botón solo de icono: la etiqueta es el nombre accesible y la ayuda emergente. */
export function BotonIcono({
  icono,
  etiqueta,
  onClick,
  tam = 16,
  activo,
  className = '',
  disabled,
  ayudaAbajo,
  ...resto
}: {
  icono: NombreIcono;
  etiqueta: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  tam?: 16 | 20;
  activo?: boolean;
  className?: string;
  disabled?: boolean;
  ayudaAbajo?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>) {
  return (
    <button
      type="button"
      className={`btn-icono ${activo ? 'btn-icono--activo' : ''} ${className}`}
      aria-label={etiqueta}
      data-ayuda={etiqueta}
      data-ayuda-pos={ayudaAbajo ? 'abajo' : undefined}
      onClick={onClick}
      disabled={disabled}
      {...resto}
    >
      <Icono nombre={icono} tam={tam} />
    </button>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

// ------------------------------------------------------------------ popover y menú
type Ubicacion = 'abajo-inicio' | 'abajo-fin' | 'arriba-inicio' | 'derecha-inicio' | 'derecha-fin';

function usePosicion(ancla: RefObject<HTMLElement | null>, panel: RefObject<HTMLElement | null>, abierto: boolean, ubicacion: Ubicacion) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const calcular = useCallback(() => {
    const a = ancla.current;
    const p = panel.current;
    if (!a || !p) return;
    const r = a.getBoundingClientRect();
    const w = p.offsetWidth;
    const h = p.offsetHeight;
    const lateral = ubicacion.startsWith('derecha');
    let left = lateral ? r.right + 10 : ubicacion === 'abajo-fin' ? r.right - w : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    let top = lateral ? (ubicacion === 'derecha-fin' ? r.bottom - h : r.top) : ubicacion === 'arriba-inicio' ? r.top - h - 4 : r.bottom + 4;
    if (!lateral && top + h > window.innerHeight - 8 && r.top - h - 4 > 8) top = r.top - h - 4;
    if (lateral) top = Math.min(top, window.innerHeight - h - 8);
    if (top < 8) top = 8;
    setPos({ top, left });
  }, [ancla, panel, ubicacion]);
  useLayoutEffect(() => {
    if (!abierto) {
      setPos(null);
      return;
    }
    calcular();
    const ro = new ResizeObserver(calcular);
    if (panel.current) ro.observe(panel.current);
    window.addEventListener('resize', calcular);
    window.addEventListener('scroll', calcular, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calcular);
      window.removeEventListener('scroll', calcular, true);
    };
  }, [abierto, calcular, panel]);
  return pos;
}

/**
 * Panel flotante anclado a un disparador. Se monta en `body` (no lo recortan contenedores con
 * desplazamiento), se cierra con Escape o clic fuera y devuelve el foco al disparador.
 */
export function Popover({
  disparador,
  children,
  ubicacion = 'abajo-inicio',
  ancho,
  className = '',
  abierto: abiertoControlado,
  alCambiar,
  etiqueta,
  rol = 'dialog',
}: {
  disparador: (props: { ref: RefObject<HTMLButtonElement>; abierto: boolean; alternar: () => void; props: Record<string, unknown> }) => ReactNode;
  children: ReactNode | ((cerrar: () => void) => ReactNode);
  ubicacion?: Ubicacion;
  ancho?: number;
  className?: string;
  abierto?: boolean;
  alCambiar?: (v: boolean) => void;
  etiqueta?: string;
  rol?: 'dialog' | 'menu' | 'listbox';
}) {
  const [interno, setInterno] = useState(false);
  const abierto = abiertoControlado ?? interno;
  const fijar = useCallback(
    (v: boolean) => {
      if (abiertoControlado === undefined) setInterno(v);
      alCambiar?.(v);
    },
    [abiertoControlado, alCambiar],
  );
  const ancla = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const pos = usePosicion(ancla, panel, abierto, ubicacion);
  const cerrar = useCallback(() => {
    fijar(false);
    requestAnimationFrame(() => ancla.current?.focus());
  }, [fijar]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || ancla.current?.contains(t)) return;
      fijar(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto, fijar]);

  useEffect(() => {
    if (!abierto || !pos) return;
    const p = panel.current;
    if (!p || p.contains(document.activeElement)) return;
    const primero = p.querySelector<HTMLElement>('[data-autofoco], input, [role=menuitem], [role=option], button, [tabindex="0"]');
    primero?.focus();
  }, [abierto, pos]);

  const alTecla = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cerrar();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
      const items = [...(panel.current?.querySelectorAll<HTMLElement>('[role=menuitem]:not([aria-disabled=true]), [role=option]:not([aria-disabled=true]), [role=menuitemcheckbox], [role=menuitemradio]') ?? [])];
      if (!items.length) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement as HTMLElement);
      let n = 0;
      if (e.key === 'ArrowDown') n = i < 0 ? 0 : (i + 1) % items.length;
      if (e.key === 'ArrowUp') n = i < 0 ? items.length - 1 : (i - 1 + items.length) % items.length;
      if (e.key === 'End') n = items.length - 1;
      items[n]?.focus();
    }
  };

  return (
    <>
      {disparador({
        ref: ancla,
        abierto,
        alternar: () => fijar(!abierto),
        props: { 'aria-haspopup': rol === 'menu' ? 'menu' : rol === 'listbox' ? 'listbox' : 'dialog', 'aria-expanded': abierto, 'aria-controls': abierto ? id : undefined },
      })}
      {abierto &&
        createPortal(
          <div
            ref={panel}
            id={id}
            className={`popover ${className}`}
            role={rol === 'dialog' ? 'dialog' : undefined}
            aria-label={etiqueta}
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: ancho, visibility: pos ? 'visible' : 'hidden' }}
            onKeyDown={alTecla}
          >
            {typeof children === 'function' ? children(cerrar) : children}
          </div>,
          // Dentro de un diálogo modal el resto del documento es inerte: el panel se monta en el diálogo.
          ancla.current?.closest('dialog') ?? document.body,
        )}
    </>
  );
}

export function ItemMenu({
  children,
  onClick,
  icono,
  peligro,
  deshabilitado,
  ayuda,
  seleccionado,
  rol = 'menuitem',
  extra,
}: {
  children: ReactNode;
  onClick?: () => void;
  icono?: ReactNode;
  peligro?: boolean;
  deshabilitado?: boolean;
  ayuda?: ReactNode;
  seleccionado?: boolean;
  rol?: 'menuitem' | 'option' | 'menuitemcheckbox' | 'menuitemradio';
  extra?: ReactNode;
}) {
  return (
    <button
      type="button"
      role={rol}
      tabIndex={-1}
      aria-disabled={deshabilitado || undefined}
      aria-checked={rol === 'menuitemcheckbox' || rol === 'menuitemradio' ? !!seleccionado : undefined}
      aria-selected={rol === 'option' ? !!seleccionado : undefined}
      className={`menu__item ${peligro ? 'menu__item--peligro' : ''} ${seleccionado ? 'menu__item--sel' : ''}`}
      onClick={() => !deshabilitado && onClick?.()}
    >
      {icono && <span className="menu__icono">{icono}</span>}
      <span className="menu__texto">
        {children}
        {ayuda && <small>{ayuda}</small>}
      </span>
      {extra}
      {seleccionado && rol !== 'menuitemcheckbox' && <Icono nombre="check" className="menu__check" />}
      {rol === 'menuitemcheckbox' && <span className={`menu__casilla ${seleccionado ? 'menu__casilla--si' : ''}`}>{seleccionado && <Icono nombre="check" tam={16} />}</span>}
    </button>
  );
}

export function SeparadorMenu({ titulo }: { titulo?: string }) {
  return titulo ? <div className="menu__grupo">{titulo}</div> : <div className="menu__sep" role="separator" />;
}
